import { effect, inject, Injectable, untracked } from '@angular/core';
import { BiometricAuth, BiometryType, CheckBiometryResult } from '@aparajita/capacitor-biometric-auth';
import { TranslateService } from '@ngx-translate/core';

import { AuthEvent } from '@core/models/auth';
import { storageSignal } from '@core/utils/signal';
import { environment } from '@environment/environment';

import { AuthService } from '../auth/auth';
import { LogService } from '../log/log';
import { PlatformService } from '../platform/platform';
import { BIOMETRIC_AVAILABLE, BIOMETRIC_ENABLED } from '../storage/storage-constant';
import { ToastService } from '../toast/toast';

@Injectable({
  providedIn: 'root'
})
export class BiometricService {
  private readonly authService = inject(AuthService);
  private readonly platformService = inject(PlatformService);
  private readonly logService = inject(LogService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly authEvent = this.authService.authEvent;

  private readonly available$ = storageSignal<boolean>(BIOMETRIC_AVAILABLE, false);
  readonly available = this.available$.value.asReadonly();
  private readonly enabled$ = storageSignal<boolean>(BIOMETRIC_ENABLED, false);
  readonly enabled = this.enabled$.value.asReadonly();

  constructor() {
    effect(() => {
      const authEvent = this.authEvent();
      untracked(() => {
        if (authEvent === AuthEvent.Logout) {
          this.enabled$.value.set(false);
        }
      });
    });
  }

  private async awaitReady() {
    await this.available$.readyPromise;
    await this.enabled$.readyPromise;
  }

  async checkBiometricsAvailable(): Promise<CheckBiometryResult | null> {
    this.logService.info('[BiometricService] checkBiometricsAvailable');
    if (environment.test) {
      this.available$.value.set(false);
      this.enabled$.value.set(false);
      this.logService.warn('[BiometricService] checkBiometricsAvailable not available in test env');
      return null;
    }
    try {
      await this.awaitReady();
      const result = await BiometricAuth.checkBiometry();
      this.available$.value.set(result.isAvailable);
      return result;
    } catch (error) {
      this.available$.value.set(false);
      this.enabled$.value.set(false);
      this.logService.warn('[BiometricService] checkBiometricsAvailable error', error);
      const header = this.translateService.instant('Common.Something_Went_Wrong');
      const message = this.translateService.instant('Biometric.Checking_Biometrics_Failed');
      this.toastService.error(header, message);
      return null;
    }
  }

  async login(authenticate: boolean = true): Promise<void> {
    this.logService.info('[BiometricService] login', { authenticate });
    await this.awaitReady();
    try {
      // Skip the native prompt under test (Karma runs on web and would block on it); still complete the load.
      if (this.enabled() && !environment.test) {
        await BiometricAuth.authenticate({
          reason: this.translateService.instant('Biometric.Sign_In_Securely'),
          cancelTitle: this.translateService.instant('Common.Cancel')
        });
      }
      await this.authService.loadAuth(authenticate);
    } catch (error) {
      this.enabled$.value.set(false);
      this.logService.warn('[BiometricService] login error', error);
      const header = this.translateService.instant('Common.Something_Went_Wrong');
      const message = this.translateService.instant('Biometric.Biometric_Login_Failed');
      this.toastService.error(header, message);
    }
  }

  async enableBiometrics() {
    this.logService.info('[BiometricService] enableBiometrics');
    try {
      const result = await this.checkBiometricsAvailable();
      if (!result?.isAvailable) {
        this.enabled$.value.set(false);
        return false;
      } else {
        await BiometricAuth.authenticate({
          reason: this.translateService.instant('Biometric.Sign_In_Securely'),
          cancelTitle: this.translateService.instant('Common.Cancel'),
          allowDeviceCredential: true
        });
        this.enabled$.value.set(true);
        return true;
      }
    } catch (error) {
      this.enabled$.value.set(false);
      this.logService.warn('[BiometricService] enableBiometrics error', error);
      const header = this.translateService.instant('Common.Something_Went_Wrong');
      const message = this.translateService.instant('Biometric.Enabling_Biometrics_Failed');
      this.toastService.error(header, message);
      return false;
    }
  }

  async disableBiometrics() {
    this.logService.info('[BiometricService] disableBiometrics');
    this.enabled$.value.set(false);
  }

  // Admin
  async toggleBiometricsAvailable(available: boolean) {
    if (environment.test) return;
    if (this.platformService.isWeb()) {
      await BiometricAuth.setBiometryType(available ? BiometryType.fingerprintAuthentication : BiometryType.none);
      await BiometricAuth.setBiometryIsEnrolled(available);
      this.available$.value.set(available);
    }
  }
}
